import { queryClient } from 'index';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { getToastErrorMessage, LogyxError } from 'services/api/errors';
import { ReactMutationKeys } from 'services/api/reactMutationKeys';
import { ReactQueryKeys } from 'services/api/reactQueryKeys';
import {
  deleteModelCompany,
  enableOrderAllCompanies,
} from 'services/ModelsCompaniesService';
import { getCompaniesForModel } from 'services/CompanyService';

export const useGetCompaniesForModel = (
  page: number,
  perPage: number,
  searchBy: string,
  modelId: number
) => {
  return useQuery({
    queryKey: [
      ReactQueryKeys.GET_COMPANIES_FOR_MODEL,
      page,
      perPage,
      searchBy,
      modelId,
    ],
    queryFn: () => {
      return getCompaniesForModel(page, perPage, searchBy, modelId);
    },
    enabled: !!modelId,
  });
};

interface IEnableAllModelOrderingForCompanyDTO {
  modelId: number;
  companyId: number;
  canOrderInternally?: boolean;
  canOrderExternally?: boolean;
}

export const useToggleOrderAllInternallyModels = () => {
  const { t } = useTranslation();
  return useMutation(
    (dto: IEnableAllModelOrderingForCompanyDTO) => {
      return enableOrderAllCompanies(
        dto.modelId,
        dto.companyId,
        dto.canOrderInternally,
        undefined
      );
    },
    {
      onSuccess: (_data, variables) => {
        toast.success(
          variables.canOrderInternally
            ? t('Enabled internal ordering for all companies')
            : t('Disabled internal ordering for all companies')
        );
        queryClient.invalidateQueries([ReactQueryKeys.GET_COMPANIES_FOR_MODEL]);
      },
      onError: (error: LogyxError) => {
        toast.error(getToastErrorMessage(error));
      },
      mutationKey: ReactMutationKeys.TOGGLE_ORDER_ALL_MODELS_INTERNALLY,
    }
  );
};

export const useToggleOrderAllExternallyModels = () => {
  const { t } = useTranslation();
  return useMutation(
    (dto: IEnableAllModelOrderingForCompanyDTO) => {
      return enableOrderAllCompanies(
        dto.modelId,
        dto.companyId,
        undefined,
        dto.canOrderExternally
      );
    },
    {
      onSuccess: (_data, variables) => {
        toast.success(
          variables.canOrderExternally
            ? t('Enabled external ordering for all companies')
            : t('Disabled external ordering for all companies')
        );
        queryClient.invalidateQueries([ReactQueryKeys.GET_COMPANIES_FOR_MODEL]);
      },
      onError: (error: LogyxError) => {
        toast.error(getToastErrorMessage(error));
      },
      mutationKey: ReactMutationKeys.TOGGLE_ORDER_ALL_MODELS_EXTERNALLY,
    }
  );
};

export const useDeleteModelCompany = () => {
  const { t } = useTranslation();
  return useMutation(
    (modelId: number) => {
      return deleteModelCompany(modelId);
    },
    {
      onSuccess: () => {
        toast.success(t('Company access revoked'));
        queryClient.invalidateQueries([ReactQueryKeys.GET_COMPANIES_FOR_MODEL]);
      },
      onError: (error: LogyxError) => {
        toast.error(getToastErrorMessage(error));
      },
      mutationKey: ReactMutationKeys.DELETE_MODEL_COMPANY,
    }
  );
};
